
// DatabaseService.js (Local SQLite - Production Ready) ✅
// Copy-Paste

import * as SQLite from 'expo-sqlite';

class DatabaseService {
  constructor() {
    this.db = SQLite.openDatabaseSync('jobeng_local.db');
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return { success: true };

    try {
      this.db.execSync(`
        PRAGMA journal_mode = WAL;
        PRAGMA foreign_keys = ON;

        CREATE TABLE IF NOT EXISTS records (
          table_name TEXT NOT NULL,
          id TEXT NOT NULL,
          json TEXT NOT NULL,
          updated_at TEXT,
          synced INTEGER DEFAULT 1,
          PRIMARY KEY (table_name, id)
        );

        CREATE INDEX IF NOT EXISTS idx_records_table ON records(table_name);
        CREATE INDEX IF NOT EXISTS idx_records_synced ON records(table_name, synced);
        CREATE INDEX IF NOT EXISTS idx_records_updated ON records(table_name, updated_at);
      `);

      this.isInitialized = true;
      return { success: true, message: 'Local database initialized' };
    } catch (e) {
      return { success: false, error: e?.message || 'DB init failed' };
    }
  }

  // ---------- Helpers ----------
  _toId(value) {
    if (value === null || value === undefined) return null;
    return String(value);
  }

  _safeStringify(obj) {
    return JSON.stringify(obj ?? {});
  }

  _safeParse(str) {
    try { return JSON.parse(str); } catch { return null; }
  }

  _normalizeRecord(tableName, record) {
    const id = this._toId(record?.id);
    if (!id) throw new Error(`Missing id for table ${tableName}`);
    const updated_at = record?.updated_at ? String(record.updated_at) : null;
    const synced = record?.synced === 0 ? 0 : 1;
    return { id, updated_at, synced, json: this._safeStringify(record) };
  }

  _buildWhere(where) {
    // Supports:
    // { synced: 0 }
    // { synced: 0, updated_at: `> 2026-01-01T...` }
    // or no where
    if (!where) return { sql: '', args: [] };

    const clauses = [];
    const args = [];

    if (where.synced !== undefined) {
      clauses.push('synced = ?');
      args.push(Number(where.synced));
    }

    if (where.updated_at && typeof where.updated_at === 'string') {
      const s = where.updated_at.trim();
      if (s.startsWith('>')) {
        clauses.push('updated_at > ?');
        args.push(s.replace(/^>\s*/, ''));
      } else if (s.startsWith('<')) {
        clauses.push('updated_at < ?');
        args.push(s.replace(/^<\s*/, ''));
      }
    }

    if (!clauses.length) return { sql: '', args: [] };
    return { sql: `WHERE ${clauses.join(' AND ')}`, args };
  }

  // ---------- CRUD ----------
  async findAll(tableName, options = {}) {
    await this.initialize();
    try {
      const { where } = options || {};
      const w = this._buildWhere(where);

      const stmt = this.db.prepareSync(
        `SELECT json FROM records WHERE table_name = ? ${w.sql ? `AND ${w.sql.replace(/^WHERE\s+/,'')}` : ''}`
      );

      const rows = stmt.executeSync([tableName, ...w.args]).getAllSync();
      const data = rows
        .map(r => this._safeParse(r.json))
        .filter(Boolean);

      return { success: true, data };
    } catch (e) {
      return { success: false, error: e?.message || 'findAll failed', data: [] };
    }
  }

  async findById(tableName, id) {
    await this.initialize();
    try {
      const _id = this._toId(id);
      if (!_id) return { success: true, data: null };

      const stmt = this.db.prepareSync(
        `SELECT json FROM records WHERE table_name = ? AND id = ? LIMIT 1`
      );
      const row = stmt.executeSync([tableName, _id]).getFirstSync();
      const data = row?.json ? this._safeParse(row.json) : null;

      return { success: true, data };
    } catch (e) {
      return { success: false, error: e?.message || 'findById failed', data: null };
    }
  }

  async create(tableName, record) {
    await this.initialize();
    try {
      const r = this._normalizeRecord(tableName, record);

      const stmt = this.db.prepareSync(
        `INSERT OR REPLACE INTO records (table_name, id, json, updated_at, synced)
         VALUES (?, ?, ?, ?, ?)`
      );
      stmt.executeSync([tableName, r.id, r.json, r.updated_at, r.synced]);

      return { success: true, data: record };
    } catch (e) {
      return { success: false, error: e?.message || 'create failed' };
    }
  }

  async insert(tableName, record) {
    return this.create(tableName, record);
  }

  async update(tableName, id, patch) {
    await this.initialize();
    try {
      const existing = await this.findById(tableName, id);
      if (!existing.success) return existing;

      const merged = { ...(existing.data || {}), ...(patch || {}), id: existing.data?.id ?? id };
      const r = this._normalizeRecord(tableName, merged);

      const stmt = this.db.prepareSync(
        `INSERT OR REPLACE INTO records (table_name, id, json, updated_at, synced)
         VALUES (?, ?, ?, ?, ?)`
      );
      stmt.executeSync([tableName, r.id, r.json, r.updated_at, r.synced]);

      return { success: true, data: merged };
    } catch (e) {
      return { success: false, error: e?.message || 'update failed' };
    }
  }

  async deleteRecord(tableName, id) {
    await this.initialize();
    try {
      const _id = this._toId(id);
      if (!_id) return { success: true };

      const stmt = this.db.prepareSync(`DELETE FROM records WHERE table_name = ? AND id = ?`);
      stmt.executeSync([tableName, _id]);

      return { success: true };
    } catch (e) {
      return { success: false, error: e?.message || 'delete failed' };
    }
  }

  async deleteFunction(tableName, id) {
    return this.deleteRecord(tableName, id);
  }

  // Optional: direct query (rarely needed)
  async query(sql, params = []) {
    await this.initialize();
    try {
      const stmt = this.db.prepareSync(sql);
      const rows = stmt.executeSync(params).getAllSync();
      return { success: true, data: rows };
    } catch (e) {
      return { success: false, error: e?.message || 'query failed', data: [] };
    }
  }

  getConfig() {
    return { isInitialized: this.isInitialized };
  }
}

const databaseService = new DatabaseService();
export default databaseService;
export { DatabaseService };
